import { Box, Button, Tooltip, Typography } from "@mui/material";
import CheckboxDropdown from "./CheckboxDropdown";
import RangeSliderDropdown from "./RangeSliderDropdown";
import InputFieldDropdown from "./InputFieldDropdown";
import RadioButtonDropdown from "./RadioButtonDropdown";
import React from "react";
import { useCumulativeCounts } from "../../utils/useCumulativeCounts";
import PublicIcon from "@mui/icons-material/Public";
import CloseIcon from "@mui/icons-material/Close";

const tooltipSx = {
  tooltip: {
    sx: {
      bgcolor: "#fff",
      color: "#393c3d",
      border: "1px solid #e0e0e0",
      borderRadius: "12px",
      boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
      maxWidth: "200px",
      fontSize: "0.78rem",
    },
  },
  arrow: {
    sx: {
      color: "#fff",
      "&::before": { filter: "drop-shadow(0px 0px 1px rgba(0,0,0,0.3))" },
    },
  },
};

interface FiltersWidgetProps {
  prices: number[];
  priceMin?: number;
  priceMax?: number;
  yearMin?: number;
  yearMax?: number;
  lengthMin?: number;
  lengthMax?: number;
  years: any[];
  lengths: number[];
  boatsFilter: string[];
  setBoatsFilter: React.Dispatch<React.SetStateAction<string[]>>;
  cabinsFilter: string[];
  setCabinsFilter: React.Dispatch<React.SetStateAction<string[]>>;
  berthsFilter: string[];
  setBerthsFilter: React.Dispatch<React.SetStateAction<string[]>>;
  bathroomFilters: string[];
  setBathroomFilter: React.Dispatch<React.SetStateAction<string[]>>;
  priceFilter: number[];
  setPriceFilter: (value: number[]) => void;
  priceUserSet?: boolean;
  yearFilter: number[];
  setyearFilter: (value: number[]) => void;
  yearUserSet?: boolean;
  lengthFilter: number[];
  setLengthFilter: (value: number[]) => void;
  lengthUserSet?: boolean;
  amenitiesFilter: string[];
  setAmenitiesFilter: React.Dispatch<React.SetStateAction<string[]>>;
  orderFilter: string;
  setOrderFilter: React.Dispatch<React.SetStateAction<string>>;
  buildersInput: string[];
  setBuildersInput: React.Dispatch<React.SetStateAction<string[]>>;
  clusterYachts: any[];
  setClusterYachts: React.Dispatch<React.SetStateAction<any[]>>;
  yachtModels: string[];
  filterCount?: number;
  onClearFilters?: () => void;
}

const FiltersWidget = ({
  prices,
  priceMin = 552,
  priceMax = 61658,
  yearMin = 1990,
  yearMax = 2025,
  lengthMin = 20,
  lengthMax = 120,
  years,
  lengths,
  boatsFilter,
  setBoatsFilter,
  cabinsFilter,
  setCabinsFilter,
  berthsFilter,
  setBerthsFilter,
  bathroomFilters,
  setBathroomFilter,
  priceFilter,
  setPriceFilter,
  priceUserSet,
  yearFilter,
  setyearFilter,
  yearUserSet,
  lengthFilter,
  setLengthFilter,
  lengthUserSet,
  amenitiesFilter,
  setAmenitiesFilter,
  orderFilter,
  setOrderFilter,
  buildersInput,
  setBuildersInput,
  setClusterYachts,
  clusterYachts,
  yachtModels,
  filterCount = 0,
  onClearFilters,
}: FiltersWidgetProps) => {
  const { counts, amenities } = useCumulativeCounts();
  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexWrap: 'wrap' }}>
      <Tooltip title="Filter by vessel type: sailboat, catamaran, motor boat" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <CheckboxDropdown
            title="Boat Type"
            selectedFilters={boatsFilter}
            setSelectedFilters={setBoatsFilter}
            options={(counts?.yacht_types ?? []).map(({ name, count }) => ({
              label: name,
              count,
            }))}
          />
        </span>
      </Tooltip>

      <Tooltip title="Number of private sleeping cabins on board" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <CheckboxDropdown
            title="Cabins"
            selectedFilters={cabinsFilter}
            setSelectedFilters={setCabinsFilter}
            options={(counts?.cabins ?? [])
              .slice()
              .sort((a, b) => parseInt(a.name) - parseInt(b.name))
              .map(({ name, count }) => ({ label: name, count }))}
          />
        </span>
      </Tooltip>

      <Tooltip title="Total sleeping capacity — number of beds" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <CheckboxDropdown
            title="Berths"
            selectedFilters={berthsFilter}
            setSelectedFilters={setBerthsFilter}
            options={(counts?.berths ?? [])
              .slice()
              .sort((a, b) => parseInt(a.name) - parseInt(b.name))
              .map(({ name, count }) => ({ label: name, count }))}
          />
        </span>
      </Tooltip>

      <Tooltip title="Number of bathrooms (WC) on board" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <CheckboxDropdown
            title="Bathrooms"
            selectedFilters={bathroomFilters}
            setSelectedFilters={setBathroomFilter}
            options={(counts?.bathrooms ?? [])
              .slice()
              .sort((a, b) => parseInt(a.name) - parseInt(b.name))
              .map(({ name, count }) => ({ label: name, count }))}
          />
        </span>
      </Tooltip>

      <Tooltip title="Total charter price for the selected week (EUR)" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <RangeSliderDropdown
            title="Price"
            min={priceMin}
            max={priceMax}
            range={priceFilter}
            setRange={setPriceFilter}
            histogramValues={prices}
            userSet={priceUserSet}
          />
        </span>
      </Tooltip>

      <Tooltip title="Year the boat was built" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <RangeSliderDropdown
            title="Year"
            min={yearMin}
            max={yearMax}
            range={yearFilter}
            setRange={setyearFilter}
            histogramValues={years}
            userSet={yearUserSet}
          />
        </span>
      </Tooltip>

      <Tooltip title="Overall length of the boat in feet" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <RangeSliderDropdown
            title="Length"
            min={lengthMin}
            max={lengthMax}
            range={lengthFilter}
            setRange={setLengthFilter}
            histogramValues={lengths}
            userSet={lengthUserSet}
          />
        </span>
      </Tooltip>

      <Tooltip title="Filter by yacht manufacturer and model" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <InputFieldDropdown
            title="Boat Model"
            selectedFilter={buildersInput}
            setSelectedFilter={setBuildersInput}
            inputValue={buildersInput}
            setInputValue={setBuildersInput}
            options={yachtModels}
          />
        </span>
      </Tooltip>

      <Tooltip title="Filter by on-board equipment and amenities" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <CheckboxDropdown
            title="Equipment"
            selectedFilters={amenitiesFilter}
            setSelectedFilters={setAmenitiesFilter}
            options={[
              { label: "Furling Mainsail" },
              ...[...amenities].filter(a => a.label.toLowerCase() !== 'furling mainsail' && !a.label.toLowerCase().includes('unknown')),
            ].sort((a, b) => a.label.localeCompare(b.label))}
          />
        </span>
      </Tooltip>

      <Tooltip title="Sort results by price, cabin count, or discount" arrow placement="top" componentsProps={tooltipSx}>
        <span>
          <RadioButtonDropdown
            title="Order by"
            selectedOption={orderFilter}
            setSelectedOption={setOrderFilter}
            options={[
              // "Recommended",
              "Lowest price",
              "Lowest cabins",
              "Highest discount",
              "Increasing Length",
            ]}
          />
        </span>
      </Tooltip>

      {clusterYachts?.length > 0 && (
        <Button
          variant="outlined"
          startIcon={<PublicIcon />}
          endIcon={<CloseIcon />}
          sx={{
            borderRadius: "9999px",
            textTransform: "none",
            backgroundColor: "#f5f5f5",
            color: "#333",
            borderColor: "#ccc",
            paddingX: 2,
            paddingY: 0.5,
            fontSize: "0.875rem",
            "&:hover": { backgroundColor: "#e0e0e0", borderColor: "#bbb" },
          }}
          onClick={() => setClusterYachts([])}
        >
          Map
        </Button>
      )}
      {filterCount > 0 && onClearFilters && (
        <Typography
          variant="body2"
          onClick={onClearFilters}
          sx={{
            alignSelf: 'center',
            cursor: 'pointer',
            color: '#1a1a2e',
            textDecoration: 'underline',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          Remove Filters
        </Typography>
      )}
    </Box>
  );
};

export default FiltersWidget;
